/* ===== Beat materials (GM Studio package 3.1) =====
 *
 * A beat's materials are an explicit, persisted list of typed references
 * (journal entries and character sheets) — the episode's cast and props. They
 * are NOT derived from journal links: adding or removing a material never
 * touches JournalLink rows, another beat's list, the prep source
 * (beat.journalEntryId) or the main map (beat.pageId).
 *
 * Before 3.1 the beat's working set was derived on the client
 * (resolveWorkingSet, removed in 3.1): the prep entry, entries linked to it either way, and the
 * characterSheetId of those. `backfillBeatMaterials` freezes that derivation
 * ONCE for a beat that has no `materials` field yet, from the campaign's own
 * data (never from a partially loaded client list): the linked entries and
 * sheets that still exist, without duplicates. The prep source and the map stay
 * in their own fields. A beat that already has the field is left untouched, so
 * the backfill is idempotent and later link changes never rebuild the list.
 */

import type { Beat, BeatMaterialRef, JournalEntryOwner, MapOwner } from './domain.js';

export const sameMaterial = (a: BeatMaterialRef, b: BeatMaterialRef): boolean =>
  a.kind === b.kind && a.id === b.id;

/** The ref is the beat's prep source (journalEntryId) — a role of its own, never also a material. */
export const isPrepSource = (beat: Pick<Beat, 'journalEntryId'>, ref: BeatMaterialRef): boolean =>
  ref.kind === 'journal' && beat.journalEntryId !== null && beat.journalEntryId !== undefined && ref.id === beat.journalEntryId;

/**
 * One record, one role in a beat: the prep source is never also one of its
 * materials (package 3.1 review R1). Returns the beat without such a ref, or
 * the same beat when there is none. The object itself is never touched.
 */
export function withoutPrepSource<B extends Pick<Beat, 'journalEntryId'> & { materials?: BeatMaterialRef[] }>(beat: B): B {
  if (!Array.isArray(beat.materials)) return beat;
  const kept = beat.materials.filter((m) => !isPrepSource(beat, m));
  return kept.length === beat.materials.length ? beat : { ...beat, materials: kept };
}

export interface BeatMaterialsBackfillSource {
  /** Journal entries of the campaign: id → the sheet the entry is bound to (if any). */
  readonly entries: ReadonlyMap<string, string | null | undefined>;
  /** Journal links of the campaign (either direction counts). */
  readonly links: ReadonlyArray<{ readonly sourceId: string; readonly targetId: string }>;
  /** Character sheets that exist in the campaign. */
  readonly sheetIds: ReadonlySet<string>;
}

/** The pre-3.1 working set of a beat as explicit materials (existing objects only, no duplicates). */
export function legacyBeatMaterials(
  beat: Pick<Beat, 'journalEntryId'>,
  source: BeatMaterialsBackfillSource,
): BeatMaterialRef[] {
  const prepId = beat.journalEntryId;
  if (prepId === null || prepId === undefined || prepId === '') return [];

  const related: string[] = [];
  for (const link of source.links) {
    const other: string | undefined = link.sourceId === prepId ? link.targetId : link.targetId === prepId ? link.sourceId : undefined;
    if (other === undefined || other === prepId || related.includes(other)) continue;
    related.push(other);
  }

  const out: BeatMaterialRef[] = [];
  const push = (ref: BeatMaterialRef): void => {
    if (!out.some((r) => sameMaterial(r, ref))) out.push(ref);
  };
  // Sheets of the prep entry and of the related entries (as resolveWorkingSet did).
  for (const entryId of [prepId, ...related]) {
    const sheetId = source.entries.get(entryId);
    if (typeof sheetId === 'string' && sheetId !== '' && source.sheetIds.has(sheetId)) {
      push({ kind: 'sheet', id: sheetId });
    }
  }
  for (const entryId of related) {
    if (source.entries.has(entryId)) push({ kind: 'journal', id: entryId });
  }
  return out;
}

/**
 * Give every beat without a `materials` field its pre-3.1 working set, and
 * reconcile a saved pair where the prep source is also a material (the source
 * keeps its role, the redundant ref is dropped — review R1; the object stays).
 * Returns the same array (and changed=false) when nothing had to change.
 */
export function backfillBeatMaterials<B extends Pick<Beat, 'journalEntryId'> & { materials?: BeatMaterialRef[] }>(
  beats: readonly B[],
  source: BeatMaterialsBackfillSource,
): { readonly beats: B[]; readonly changed: boolean } {
  let changed = false;
  const next = beats.map((beat) => {
    const filled = Array.isArray(beat.materials) ? beat : { ...beat, materials: legacyBeatMaterials(beat, source) };
    const reconciled = withoutPrepSource(filled);
    if (reconciled !== beat) changed = true;
    return reconciled;
  });
  return { beats: changed ? next : [...beats], changed };
}

/* ===== Owner of a journal entry / a map (P03) =====
 *
 * The owner is the GM's organisation of preparation: a note or a map that
 * lives in one beat or at one quest objective stays out of the Library lists.
 * An absent owner means «shared» (entries of old clients, players, the AI).
 * The owner is never sent to players (data-visibility-contract §1, §11).
 */


const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

export function isJournalEntryOwner(value: unknown): value is JournalEntryOwner {
  if (typeof value !== 'object' || value === null) return false;
  const o = value as Record<string, unknown>;
  switch (o.kind) {
    case 'shared': return true;
    case 'beat': return isNonEmptyString(o.beatId);
    case 'objective': return isNonEmptyString(o.questId) && isNonEmptyString(o.objectiveId);
    case 'quest': return isNonEmptyString(o.questId);
    default: return false;
  }
}

export function isMapOwner(value: unknown): value is MapOwner {
  if (typeof value !== 'object' || value === null) return false;
  const o = value as Record<string, unknown>;
  return o.kind === 'shared' || (o.kind === 'beat' && isNonEmptyString(o.beatId));
}

/** A stored owner as read back from the database / the wire; anything else is «not stored». */
export function readJournalEntryOwner(value: unknown): JournalEntryOwner | undefined {
  return isJournalEntryOwner(value) ? normalizeOwner(value) : undefined;
}

export function readMapOwner(value: unknown): MapOwner | undefined {
  return isMapOwner(value) ? normalizeOwner(value) : undefined;
}

/** Only the contract's fields — nothing a client added rides along. */
function normalizeOwner<T extends JournalEntryOwner | MapOwner>(owner: T): T {
  const o = owner as JournalEntryOwner;
  switch (o.kind) {
    case 'shared': return { kind: 'shared' } as T;
    case 'beat': return { kind: 'beat', beatId: o.beatId } as T;
    case 'objective': return { kind: 'objective', questId: o.questId, objectiveId: o.objectiveId } as T;
    case 'quest': return { kind: 'quest', questId: o.questId } as T;
  }
}

/** No owner, or explicitly shared. */
export const isSharedOwner = (owner: { readonly kind: string } | null | undefined): boolean =>
  owner === undefined || owner === null || owner.kind === 'shared';

export interface OwnerMigrationEntry {
  readonly id: string;
  readonly type: string;
  readonly owner?: JournalEntryOwner | undefined;
}

/**
 * Q-2 / P-1: owners for entries that have none stored yet. Run ONCE per entry —
 * the caller stores every returned owner (shared included), so an entry the GM
 * later moved to the shared list is never given a beat again.
 *
 * - a prep text (beat.journalEntryId) of exactly one beat → that beat;
 * - a prep text of several beats (decision 6: never cut) → the one quest that
 *   is among the materials of ALL those beats; no such quest, or several →
 *   shared (the question is open);
 * - everything else → shared.
 *
 * Entries that already have an owner are not in the result.
 */
export function initialOwners(
  entries: ReadonlyArray<OwnerMigrationEntry>,
  beats: ReadonlyArray<Pick<Beat, 'id' | 'journalEntryId' | 'materials'>>,
): Map<string, JournalEntryOwner> {
  const beatsByPrep = new Map<string, Array<Pick<Beat, 'id' | 'journalEntryId' | 'materials'>>>();
  for (const beat of beats) {
    if (beat.journalEntryId === null || beat.journalEntryId === undefined || beat.journalEntryId === '') continue;
    const list = beatsByPrep.get(beat.journalEntryId) ?? [];
    list.push(beat);
    beatsByPrep.set(beat.journalEntryId, list);
  }
  const quests = entries.filter((e) => e.type === 'quest');
  const result = new Map<string, JournalEntryOwner>();
  for (const entry of entries) {
    if (entry.owner !== undefined) continue;
    // Q-2: quests and NPCs are shared by default — an encounter quest an import made a beat's
    // journalEntryId is still a quest of the Library, not a note hidden in that beat.
    const users = entry.type === 'quest' || entry.type === 'npc' ? [] : beatsByPrep.get(entry.id) ?? [];
    if (users.length === 1) {
      result.set(entry.id, { kind: 'beat', beatId: users[0]!.id });
      continue;
    }
    if (users.length > 1) {
      const common = quests.filter((q) => q.id !== entry.id
        && users.every((b) => (b.materials ?? []).some((m) => m.kind === 'journal' && m.id === q.id)));
      result.set(entry.id, common.length === 1 ? { kind: 'quest', questId: common[0]!.id } : { kind: 'shared' });
      continue;
    }
    result.set(entry.id, { kind: 'shared' });
  }
  return result;
}
